import { constructUrl } from './constructUrl';
import type {
  ITeamUpdatePost,
  TeamUpdateAttachment,
  TeamUpdateImageAttachment,
  TeamUpdateTranscriptStatus,
  TeamUpdateVoiceAttachment,
} from '../types/TeamUpdate';

/** Flat media fields written by the Expo app (and dual-written by Admin). */
export type TeamUpdateFlatMediaFields = {
  attachments?: TeamUpdateAttachment[];
  imageUrls?: string[];
  imagePaths?: string[];
  audioUrl?: string;
  audioPath?: string;
  transcription?: string;
  transcriptionStatus?: TeamUpdateTranscriptStatus;
  createdAt?: unknown;
};

function isAbsoluteMediaUrl(value: string): boolean {
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('file://') ||
    value.startsWith('blob:')
  );
}

function urlFromStoragePath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  if (isAbsoluteMediaUrl(trimmed)) return trimmed;
  // Admin-gated prefix needs a tokenised downloadUrl — do not invent one.
  if (trimmed.startsWith('team-updates/')) return '';
  return constructUrl(trimmed);
}

/** Prefer a terminal status from either the flat fields or the attachment. */
export function coalesceTranscriptStatus(
  ...statuses: Array<TeamUpdateTranscriptStatus | undefined>
): TeamUpdateTranscriptStatus | undefined {
  if (statuses.includes('ready')) return 'ready';
  if (statuses.includes('failed')) return 'failed';
  if (statuses.includes('pending')) return 'pending';
  return undefined;
}

const STALE_PENDING_MS = 3 * 60 * 1000;

function createdAtMs(createdAt: unknown): number | null {
  if (!createdAt || typeof createdAt !== 'object') return null;
  const seconds =
    (createdAt as { seconds?: number; _seconds?: number }).seconds ??
    (createdAt as { _seconds?: number })._seconds;
  return typeof seconds === 'number' ? seconds * 1000 : null;
}

/** Pending transcripts older than a few minutes are treated as failed in the UI. */
export function resolveDisplayTranscriptStatus(
  status: TeamUpdateTranscriptStatus | undefined,
  createdAt?: unknown
): TeamUpdateTranscriptStatus | undefined {
  if (status !== 'pending') return status;
  const created = createdAtMs(createdAt);
  if (created === null) return status;
  if (Date.now() - created > STALE_PENDING_MS) return 'failed';
  return status;
}

export function buildAttachmentsFromFlatFields(
  fields: TeamUpdateFlatMediaFields
): TeamUpdateAttachment[] {
  const attachments: TeamUpdateAttachment[] = [];
  const urls = (fields.imageUrls || []).filter(Boolean);
  const paths = (fields.imagePaths || []).filter(Boolean);
  const count = Math.max(urls.length, paths.length);

  for (let i = 0; i < count; i += 1) {
    const downloadUrl = urls[i] || urlFromStoragePath(paths[i] || '');
    const storagePath = paths[i] || '';
    if (!downloadUrl && !storagePath) continue;
    attachments.push({
      type: 'image',
      downloadUrl: downloadUrl || storagePath,
      storagePath,
    });
  }

  if (fields.audioUrl || fields.audioPath) {
    const downloadUrl =
      fields.audioUrl?.trim() || urlFromStoragePath(fields.audioPath || '');
    const storagePath = fields.audioPath?.trim() || '';
    if (!downloadUrl && !storagePath) return attachments;
    if (!downloadUrl && storagePath.startsWith('team-updates/')) return attachments;
    attachments.push({
      type: 'voice',
      downloadUrl: downloadUrl || storagePath,
      storagePath,
      durationMs: 0,
      transcriptStatus: fields.transcriptionStatus || 'pending',
      ...(fields.transcription ? { transcript: fields.transcription } : {}),
    });
  }

  return attachments;
}

function mergeVoiceTranscriptState(
  attachments: TeamUpdateAttachment[],
  fields: TeamUpdateFlatMediaFields
): TeamUpdateAttachment[] {
  const voice = attachments.find(
    (item): item is TeamUpdateVoiceAttachment => item.type === 'voice'
  );
  if (!voice) return attachments;

  const transcript =
    fields.transcription?.trim() || voice.transcript?.trim() || undefined;
  const transcriptStatus = resolveDisplayTranscriptStatus(
    coalesceTranscriptStatus(fields.transcriptionStatus, voice.transcriptStatus),
    fields.createdAt
  );

  return attachments.map((item) => {
    if (item.type !== 'voice') return item;
    return {
      ...item,
      ...(transcriptStatus ? { transcriptStatus } : {}),
      ...(transcript ? { transcript } : {}),
    };
  });
}

/** Ensure Admin UI always has `attachments`, including for Expo-authored flat media. */
export function normaliseTeamUpdatePostMedia<T extends TeamUpdateFlatMediaFields>(
  post: T
): T & { attachments?: TeamUpdateAttachment[] } {
  const base = post.attachments?.length
    ? post.attachments
    : buildAttachmentsFromFlatFields(post);
  if (!base.length) return post;
  const attachments = mergeVoiceTranscriptState(base, post);
  return { ...post, attachments };
}

export function normaliseTeamUpdateReplyMedia<T extends TeamUpdateFlatMediaFields>(
  reply: T
): T & { attachments?: TeamUpdateAttachment[] } {
  return normaliseTeamUpdatePostMedia(reply);
}

/** Flat fields dual-written alongside Admin `attachments` for Expo recipients. */
export function flatMediaFieldsFromAttachments(
  attachments: TeamUpdateAttachment[] | undefined
): Pick<
  ITeamUpdatePost,
  | 'imageUrls'
  | 'imagePaths'
  | 'audioUrl'
  | 'audioPath'
  | 'transcription'
  | 'transcriptionStatus'
> {
  if (!attachments?.length) return {};

  const images = attachments.filter(
    (item): item is TeamUpdateImageAttachment => item.type === 'image'
  );
  const voice = attachments.find(
    (item): item is TeamUpdateVoiceAttachment => item.type === 'voice'
  );

  return {
    ...(images.length
      ? {
          imageUrls: images.map((image) => image.downloadUrl).filter(Boolean),
          imagePaths: images.map((image) => image.storagePath).filter(Boolean),
        }
      : {}),
    ...(voice
      ? {
          audioUrl: voice.downloadUrl,
          audioPath: voice.storagePath,
          transcriptionStatus: voice.transcriptStatus,
          ...(voice.transcript ? { transcription: voice.transcript } : {}),
        }
      : {}),
  };
}
